import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  Button,
  Typography,
  Dialog,
  InputAdornment,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  CircularProgress,
  AlertColor
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMessages } from 'modules/intl';
import { ConnectorInfo } from 'types/types';
import { postRequest } from 'utils/helper';
import { ErrorPage } from 'modules/common/routes/ErrorPage';
import { ToastSnackBar } from '../Setting/ToastSnackBar';
import { DialogHeaderContext } from './DialogHeader';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    marginTop: theme.spacing(2.5)
  },
  box: {
    display: 'flex',
    justifyContent: 'center'
  },
  textField: {
    marginBottom: theme.spacing(3.75)
  }
}));

type BamboohrInstallPageProps = {
  info: ConnectorInfo;
  open: boolean;
  handleInstallDialogClose: () => void;
};

const messages = ['intermediateInstallDialog.inputError'];

export const BamboohrInstallPage: React.FC<BamboohrInstallPageProps> = ({
  info,
  open,
  handleInstallDialogClose
}: BamboohrInstallPageProps) => {
  const [textField, setTextField] = useState({
    apiKey: '',
    subDomain: ''
  });
  const [textFieldError, setTextFieldError] = useState({
    apiKeyError: '',
    subDomainError: ''
  });
  const [connected, setConnected] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
  const [showErrorPage, setShowErrorPage] = useState<boolean>(false);

  const classes = useStyles();
  const errorMessage = useMessages(messages);
  const { formatMessage } = useIntl();

  const requiredInputError =
    errorMessage['intermediateInstallDialog.inputError'];

  const handleError = useCallback(
    (field: string, value: string) => {
      setTextFieldError((prev: any) => ({
        ...prev,
        [`${field}Error`]: value.trim() === '' ? requiredInputError : ''
      }));
    },
    [requiredInputError]
  );

  const handleChange = useCallback(
    (field: string, event: any) => {
      const value = event.target.value;
      setTextField((prev: any) => ({
        ...prev,
        [field]: value
      }));
      handleError(field, value);
    },
    [handleError]
  );

  const onSnackbarClose = useCallback(() => {
    setIsSnackBarOpen(false);
  }, []);
  const onSnackbarOpen = useCallback(() => {
    setIsSnackBarOpen(true);
  }, []);

  const handleApiCall = useCallback(async () => {
    setIsDisabled(true);
    setSeverity('success');
    onSnackbarOpen();
    try {
      const body = {
        connectorName: info.shortName,
        companyKey: info.companyKey,
        environment: 'production',
        companyDomain: textField.subDomain,
        apiKey: textField.apiKey
      };
      const res = await postRequest(
        '/integration-settings-api/install-tokenbased-connector',
        body,
        false
      );
      if (res.data) {
        setConnected(true);
      }
    } catch (error: any) {
      if (error?.response?.status === 500) {
        setShowErrorPage(true);
      } else {
        setIsDisabled(false);
        setSeverity('error');
        onSnackbarOpen();
      }
    }
  }, [
    info.companyKey,
    info.shortName,
    textField.subDomain,
    textField.apiKey,
    onSnackbarOpen
  ]);

  const validateAndConnect = (event: any) => {
    event.preventDefault();
    const value = event.target.value;

    let hasError = false;
    const fields: Array<keyof typeof textField> = ['apiKey', 'subDomain'];

    fields.forEach((field) => {
      if (textField[field].trim() === '') {
        handleError(field, value);
        hasError = true;
      }
    });

    if (!hasError) {
      setIsDisabled(true);
      handleApiCall();
    }
  };

  /* eslint-disable indent */
  const alertMessage = useMemo(() => {
    return severity === 'error'
      ? formatMessage({
          id: 'intermediateInstallDialog.snackBarErrorMessage'
        })
      : formatMessage({
          id: 'intermediateInstallDialog.waitingMessage'
        });
  }, [formatMessage, severity]);

  setTimeout(() => {
    if (connected) {
      window.location.reload();
    }
  }, 2000);

  if (showErrorPage) {
    return <ErrorPage open={true} />;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleInstallDialogClose}
        data-testid="check-all-contents"
      >
        <DialogTitle
          className={classes.header}
          data-testid="check-dialog-title"
        >
          <FormattedMessage id={'intermediateInstallDialog.header'} />
        </DialogTitle>
        <DialogContent>
          <DialogHeaderContext shortName={info.shortName} />
          <Box className={classes.box} data-testid="render-box">
            <div>
              <TextField
                id={'apiToken'}
                variant="outlined"
                required
                fullWidth
                value={textField.apiKey}
                onChange={(event) => handleChange('apiKey', event)}
                error={Boolean(textFieldError.apiKeyError)}
                label={
                  <FormattedMessage
                    id={'intermediateInstallDialog.apiToken.label'}
                  />
                }
                type={'password'}
                helperText={
                  textFieldError.apiKeyError ? (
                    textFieldError.apiKeyError
                  ) : (
                    <FormattedMessage
                      id={'intermediateInstallDialog.apiToken.helperText'}
                    />
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      data-testid="check-apiKey-startAdornment"
                    >
                      <LockIcon />
                    </InputAdornment>
                  )
                }}
                className={classes.textField}
                inputProps={{ 'data-testid': 'apiTokenInput' }}
              />
              <br />
              <TextField
                id="subDomain"
                variant="outlined"
                required
                fullWidth
                value={textField.subDomain}
                onChange={(event) => handleChange('subDomain', event)}
                error={Boolean(textFieldError.subDomainError)}
                label={
                  <FormattedMessage
                    id={'intermediateInstallDialog.subDomain.label'}
                  />
                }
                type={'text'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        variant="body1"
                        data-testid="check-subdomain-startAdornment"
                      >
                        <FormattedMessage
                          id={
                            'intermediateInstallDialog.subDomain.startAdornment'
                          }
                        />
                      </Typography>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        variant="body1"
                        data-testid="check-subdomain-endAdornment"
                      >
                        <FormattedMessage
                          id={
                            'intermediateInstallDialog.subDomain.endAdornment'
                          }
                        />
                      </Typography>
                    </InputAdornment>
                  )
                }}
                helperText={
                  textFieldError.subDomainError ? (
                    textFieldError.subDomainError
                  ) : (
                    <FormattedMessage
                      id={'intermediateInstallDialog.subDomain.helperText'}
                    />
                  )
                }
                className={classes.textField}
                inputProps={{ 'data-testid': 'subDomainInput' }}
              />
              <br />
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleInstallDialogClose}
            color="grey"
            autoFocus
            variant="contained"
            disabled={isDisabled}
            data-testid="cancel-button"
          >
            <FormattedMessage id={'connectorSetting.cancel'} />
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={validateAndConnect}
            disabled={isDisabled}
            data-testid="connect-button"
          >
            {isDisabled ? (
              <Box display="flex" alignItems="center">
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                <FormattedMessage id="intermediateInstallDialog.installing" />
              </Box>
            ) : (
              <FormattedMessage
                id={'intermediateInstallDialog.connectButton'}
              />
            )}
          </Button>
        </DialogActions>
        <ToastSnackBar
          show={isSnackBarOpen}
          category={severity}
          message={alertMessage}
          onClose={onSnackbarClose}
        />
      </Dialog>
    </>
  );
};
